/**
 * Schedule - Chart Audit — who gets the page.
 *
 * ⛔ KEEP THIS FILE PURE AND IMPORT-FREE. cypress/node-tests executes it verbatim, the same way it
 * executes liveness.js and the other scheduleView utils. One `import` makes it unloadable there and
 * the agreement guards silently stop running.
 *
 * Plan: docs/plans/2026-10-28-schedule-chart-audit-release-plan.md (D2, D3, D4, WP2.1)
 *
 * ── THE RULE ──────────────────────────────────────────────────────────────────────────────────
 * An OFFICE user gets the nav entry and the page when ALL of these hold:
 *   1. the office's `SCHEDULE_VIEW_ENABLED` setting is exactly 'TRUE'   (the SuperAdmin switch)
 *   2. the OWNER site's Teamio app (exe) is at least SCHEDULE_CHART_AUDIT_MIN_EXE
 *   3. the role is not Outsourced                                        (the same rule as Forms)
 * A blank, missing or unparseable exe version FAILS CLOSED (it is not "at least" anything). A Super
 * Admin always has the page (src/config/ability.js) and sees a banner when the office does not
 * qualify — see describeAccessNotice.
 *
 * ── THE SERVICE DECIDES FOR REAL ──────────────────────────────────────────────────────────────
 * This is the convenience layer (nav, route, banner). The real gate is schedule-view-service
 * lib/access.js, which refuses every data route with 403 { unavailable, exeVersion, minExeVersion }
 * — it has no role exception, so a Super Admin on a non-qualifying office sees the banner over an
 * empty schedule. The two copies of the rule are pinned together by
 * cypress/node-tests/schedule-view-access.test.js (the version compare against teamio-admin
 * lib/exe-table-overrides.js, the minimum against the service's lib/access.js).
 *
 * ── THE ORDER OF THE REASONS MATCHES THE SERVICE ──────────────────────────────────────────────
 * The version is checked BEFORE the switch: an office below the minimum cannot be switched on from
 * SuperAdmin at all, so "needs the app update" is the blocker to name whatever the switch says.
 */

/**
 * The lowest Teamio app (exe) version whose office may use the page.
 *
 * ⛔ NOT teamio-admin's EXE_SETTINGS_MIN_VERSION (3.0.56, src/utils/exeLiveSettings.js): that gates
 * the "Refresh Exe Settings" command, a different thing. One value here, in schedule-view-service
 * lib/access.js and in teamio-admin; the agreement tests pin them together.
 */
export const SCHEDULE_CHART_AUDIT_MIN_EXE = '3.0.61';

/** The teamio.settings property that is the per-office switch (also read by the exe for live updates). */
export const SCHEDULE_ACCESS_SETTING = 'SCHEDULE_VIEW_ENABLED';

/** The name the page, the nav entry and the SuperAdmin card all use. */
export const SCHEDULE_PAGE_NAME = 'Schedule - Chart Audit';

/** Why an office is refused. The first two are the codes the service sends in its 403. */
export const ACCESS_REASONS = Object.freeze({
  NOT_ENABLED: 'NOT_ENABLED',
  EXE_TOO_OLD: 'EXE_TOO_OLD',
  // Local only: the service has no role exception, so it never answers this.
  NOT_FOR_ROLE: 'NOT_FOR_ROLE',
});

/**
 * '3.0.61' -> [3, 0, 61]. Tolerates a leading 'v' and surrounding whitespace. Garbage -> null.
 * A copy of teamio-admin lib/exe-table-overrides.js parseVersion (agreement-tested).
 */
export function parseVersion(version) {
  if (typeof version !== 'string' && typeof version !== 'number') return null;
  const text = String(version).trim().replace(/^v/i, '');
  if (text === '') return null;
  const parts = text.split('.');
  const nums = [];
  for (let i = 0; i < parts.length; i += 1) {
    if (!/^\d+$/.test(parts[i])) return null;
    nums.push(parseInt(parts[i], 10));
  }
  return nums.length ? nums : null;
}

/**
 * Numeric dotted comparison — '3.0.9' is BELOW '3.0.61' (a string compare gets this backwards).
 * A copy of teamio-admin lib/exe-table-overrides.js compareVersions (agreement-tested).
 * @returns {-1|0|1|null} null when either side is missing or garbage.
 */
export function compareVersions(a, b) {
  const left = parseVersion(a);
  const right = parseVersion(b);
  if (!left || !right) return null;
  const len = Math.max(left.length, right.length);
  for (let i = 0; i < len; i += 1) {
    const l = left[i] === undefined ? 0 : left[i];
    const r = right[i] === undefined ? 0 : right[i];
    if (l < r) return -1;
    if (l > r) return 1;
  }
  return 0;
}

/** Fails CLOSED: a missing or unreadable version is NOT at least the minimum. */
export function isVersionAtLeast(version, minimum = SCHEDULE_CHART_AUDIT_MIN_EXE) {
  const cmp = compareVersions(version, minimum);
  return cmp === 0 || cmp === 1;
}

function isActive(row) {
  return Number(row && row.Active) === 1 || (row && row.Active === true);
}

/** ClinicNum 0 = the HQ. A missing ClinicNum is NOT 0 (Number(null) would say it is). */
function isHq(row) {
  if (!row || row.ClinicNum === null || row.ClinicNum === undefined || row.ClinicNum === '') return false;
  return Number(row.ClinicNum) === 0;
}

/**
 * The site whose Teamio app serves this office: the active HQ (ClinicNum 0) when the customer has
 * more than one active site, else the site itself. The same rule as teamio-admin admin.js
 * `resolveExeOwnerSiteID` and schedule-view-service lib/access.js `chooseOwner` — one exe serves a
 * whole multi-clinic customer and reports its version on the HQ row. Prod has no multi-clinic
 * customer today; the rule is here so the day one exists the page does not look at a clinic row
 * that no exe ever updates.
 *
 * @param {Array} sites   the customer's teamio.sites rows as the login loads them (store `customer.sites`)
 * @param {string} siteID the signed-in site
 * @returns {object|null} the owner's row, or null when the signed-in site is not among the rows
 */
export function ownerSiteRow(sites, siteID) {
  const list = Array.isArray(sites) ? sites.filter(row => row && typeof row === 'object') : [];
  const self = list.find(row => row.SiteID === siteID);
  if (!self) return null;

  const sameCustomer = list.filter(row => row.CustomerID === self.CustomerID);
  if (sameCustomer.filter(isActive).length > 1) {
    const hq = sameCustomer.find(row => isActive(row) && isHq(row));
    if (hq) return hq;
  }
  return self;
}

/** The owner site's ExeVersion as a trimmed string, or null when unknown. */
export function ownerExeVersion(sites, siteID) {
  const row = ownerSiteRow(sites, siteID);
  if (!row) return null;
  const raw = row.ExeVersion;
  if (raw === null || raw === undefined || String(raw).trim() === '') return null;
  return String(raw).trim();
}

/**
 * The office rule.
 *
 * @param {{switchValue?: *, exeVersion?: *, role?: string}} input
 *        switchValue — the office's SCHEDULE_VIEW_ENABLED PropertyValue (exactly 'TRUE' counts)
 *        exeVersion  — the OWNER site's ExeVersion (ownerExeVersion)
 *        role        — the signed-in user's role
 * @returns {{allowed: boolean, reason: string|null, exeVersion: string|null, minExeVersion: string}}
 *          `reason` is null when allowed; otherwise an ACCESS_REASONS code.
 */
export function scheduleAccess({ switchValue, exeVersion, role } = {}) {
  const shown = (exeVersion === null || exeVersion === undefined || String(exeVersion).trim() === '')
    ? null
    : String(exeVersion).trim();
  const base = { exeVersion: shown, minExeVersion: SCHEDULE_CHART_AUDIT_MIN_EXE };

  if (role === 'Outsourced') return { allowed: false, reason: ACCESS_REASONS.NOT_FOR_ROLE, ...base };
  if (!isVersionAtLeast(shown)) return { allowed: false, reason: ACCESS_REASONS.EXE_TOO_OLD, ...base };
  if (switchValue !== 'TRUE') return { allowed: false, reason: ACCESS_REASONS.NOT_ENABLED, ...base };
  return { allowed: true, reason: null, ...base };
}

/**
 * The Super Admin's banner sentence for an office that does not qualify ('' when it does, or when
 * the reason is not one a banner explains). Takes the service's 403 body as it is — { unavailable,
 * exeVersion, minExeVersion } — or the object scheduleAccess returns ({ reason, ... }).
 *
 * @returns {string}
 */
export function describeAccessNotice(info) {
  if (!info || typeof info !== 'object') return '';
  const reason = info.unavailable || info.reason;
  const head = `This office can't use ${SCHEDULE_PAGE_NAME} yet: `;

  if (reason === ACCESS_REASONS.EXE_TOO_OLD) {
    const min = (typeof info.minExeVersion === 'string' && info.minExeVersion.trim())
      ? info.minExeVersion.trim()
      : SCHEDULE_CHART_AUDIT_MIN_EXE;
    const have = (info.exeVersion === null || info.exeVersion === undefined || String(info.exeVersion).trim() === '')
      ? 'version unknown'
      : String(info.exeVersion).trim();
    return `${head}Teamio app ${have} (needs ${min}+)`;
  }
  if (reason === ACCESS_REASONS.NOT_ENABLED) return `${head}switched off in SuperAdmin`;
  return '';
}

/**
 * The service's refusal, read off a failed axios call: 403 with a body carrying `unavailable`.
 * Anything else (a network error, a 500, a 403 from some other gate) is NOT a refusal and returns
 * null, so it stays the error it was.
 *
 * @returns {{unavailable: string, exeVersion: string|null, minExeVersion: string}|null}
 */
export function accessRefusalFrom(err) {
  const res = err && err.response;
  if (!res || res.status !== 403) return null;
  const body = res.data;
  if (!body || typeof body !== 'object') return null;
  if (typeof body.unavailable !== 'string' || body.unavailable === '') return null;
  const exe = body.exeVersion;
  const min = body.minExeVersion;
  return {
    unavailable: body.unavailable,
    exeVersion: (exe === null || exe === undefined || String(exe).trim() === '') ? null : String(exe).trim(),
    minExeVersion: (typeof min === 'string' && min.trim()) ? min.trim() : SCHEDULE_CHART_AUDIT_MIN_EXE,
  };
}

/**
 * The SuperAdmin card's rule for turning the switch ON: refused below the minimum (or when the
 * version is unknown). Turning OFF is never refused — a caller asks this only for newValue === true.
 *
 * @returns {{allowed: boolean, message: string}} `message` is the small red text under the switch.
 */
export function canSwitchOn(exeVersion) {
  if (isVersionAtLeast(exeVersion)) return { allowed: true, message: '' };
  const have = (exeVersion === null || exeVersion === undefined || String(exeVersion).trim() === '')
    ? 'unknown'
    : String(exeVersion).trim();
  return {
    allowed: false,
    message: `Needs Teamio app ${SCHEDULE_CHART_AUDIT_MIN_EXE}+ (this office: ${have})`,
  };
}
